<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from "vue";

const props = defineProps<{
  modelValue: string;
  placeholder?: string;
  minRows?: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

const adjustHeight = () => {
  const textarea = textareaRef.value;
  if (!textarea) return;

  // Reset height to auto to get the correct scrollHeight
  textarea.style.height = "auto";
  // Set height to scrollHeight
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit("update:modelValue", target.value);
  adjustHeight();
};

// Adjust height when value changes externally
watch(
  () => props.modelValue,
  () => {
    nextTick(adjustHeight);
  },
);

// Initial adjustment
onMounted(() => {
  nextTick(adjustHeight);
});
</script>

<template>
  <textarea
    ref="textareaRef"
    class="field-input"
    :value="modelValue"
    :placeholder="placeholder"
    :rows="minRows ?? 2"
    @input="handleInput"
  />
</template>

<style scoped lang="scss">
.field-input {
  width: 100%;
  padding: 16px;
  border: none;
  border-radius: 16px;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  font-size: 16px;
  font-family: inherit;
  resize: none;

  &::placeholder {
    color: var(--bs-secondary-color);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--bs-primary);
  }
}
</style>
